import React from 'react';
import PropTypes from 'prop-types';
import { TabGroup, TabPanel } from '@els/els-react--tab-group';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import { OptionSwitch, getCheckedValue } from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import { getPredictedProcessingTimeIntervalUpdatedValue, getPredictedProcessingTimeLabelByValue } from '@Pages/RetroSynthesis/utils';
import { PREDICTED_PARAMS_IKTOS_GENERAL, PREDICTED_PARAMS_IKTOS_INTERMEDIATES, RCS10D, RCSANY } from './constants';
import PredictedParametersIktosIntermediates from './PredictedParametersIktosIntermediates';
import PredictedParametersIktosGeneral from './PredictedParametersIktosGeneral';
import { PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS, ProjectSynthesisPartner } from '../constants';

const IKTOS_LENGTH_OF_ROUTES = 'RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS';
const IKTOS_SELECTIVITY = 'RX2_PREDICTION_PLAN_IKTOS_REGIO';
const IKTOS_STEREO = 'RX2_PREDICTION_PLAN_IKTOS_STEREO';
const USED_BUILDING_BLOCKS = 'RX2_PREDICTION_PLAN_LIBRARIES';
const IKTOS_MAX_PRICE_PER_GRAM = 'RX2_PREDICTION_PLAN_IKTOS_PRICE';
const IKTOS_PROCESSING_TIME = 'RX2_PREDICTION_PLAN_IKTOS_PROCESSING_TIME';

const getBlockArrayValues = (optionName, resp = []) => {
  // eslint-disable-next-line no-shadow
  const optionSet = find(resp, (optionSet) => optionSet && optionSet.name === optionName) || {};
  const values =
    optionSet.preferences &&
    optionSet.preferences.map((element) => ({
      name: element.name,
      label: element.label,
      values: element.values,
    }));
  return values || [];
};

const extractSelectedBuildingBlocks = (optionName, resp = []) => {
  // eslint-disable-next-line no-shadow
  const optionSet = find(resp, (optionSet) => optionSet && optionSet.name === optionName) || {};
  const values =
    (optionSet.preferences &&
      optionSet.preferences.filter((element) => {
        if (!element.values) {
          return false;
        }

        /* If option has only one value - use this value,
         * if more than one - check if "checked" is defined:
         *   if yes - use checked
         *   if no - use defaultValue
         */
        const activeValue =
          element.values.length === 1
            ? element.values[0]
            : element.values.find((value) => (!isUndefined(value.checked) ? value.checked : value.defaultValue));
        return activeValue && activeValue.value === OptionSwitch.ON;
      })) ||
    [];
  return values.map((element) => element.name) || [];
};

const extractCheifActiveValues = (optionName, resp = []) => {
  // eslint-disable-next-line no-shadow
  const optionSet = find(resp, (optionSet) => optionSet && optionSet.name === optionName) || {};
  if (optionSet.values === undefined) return undefined;
  const activeValue =
    optionSet.values.length === 1
      ? optionSet.values[0]
      : optionSet.values.find((value) => (!isUndefined(value.checked) ? value.checked : value.defaultValue));
  return activeValue && activeValue.value;
};

export const extractDefaultPredictedParametersIktos = (optionResponse) => {
  if (!optionResponse) {
    return;
  }
  const extractedUsedBuildingBlocks = extractSelectedBuildingBlocks(USED_BUILDING_BLOCKS, optionResponse) || [];
  const extractedMaxPricePerGram = extractCheifActiveValues(IKTOS_MAX_PRICE_PER_GRAM, optionResponse);
  const extractedStereo = extractCheifActiveValues(IKTOS_STEREO, optionResponse);

  // eslint-disable-next-line consistent-return
  return {
    maxNoOfSteps: extractCheifActiveValues(IKTOS_LENGTH_OF_ROUTES, optionResponse) || '15',
    firstDisconnect: extractCheifActiveValues(IKTOS_LENGTH_OF_ROUTES, optionResponse) === '1',
    selectivity: `${extractCheifActiveValues(IKTOS_SELECTIVITY, optionResponse)}` === OptionSwitch.ON,
    poolChiral: extractedStereo === undefined ? true : `${extractedStereo}` === OptionSwitch.OFF,
    maxPricePerGram: extractedMaxPricePerGram && `${extractedMaxPricePerGram}` !== '0' ? extractedMaxPricePerGram : '',
    usedBuildingBlocks: extractedUsedBuildingBlocks,
    isRCSUnchecked: !(extractedUsedBuildingBlocks.indexOf(RCS10D) >= 0 || extractedUsedBuildingBlocks.indexOf(RCSANY) >= 0),
    includeSubstructures: [],
    excludeSubstructures: [],
    processingTime: getPredictedProcessingTimeIntervalUpdatedValue(
      parseInt(getCheckedValue(IKTOS_PROCESSING_TIME, optionResponse), 10),
      PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS
    ),
    partner: ProjectSynthesisPartner.IKTOS,
  };
};

function PredictedParametersIktos({ optionResponse, onParamsChanged, projectParams, editable, tooltipFor, handleValidationError }) {
  const buildingBlockElements = getBlockArrayValues(USED_BUILDING_BLOCKS, optionResponse);
  const defaultMaxNoOfSteps = Number(extractCheifActiveValues(IKTOS_LENGTH_OF_ROUTES, optionResponse) || '15');

  return (
    <>
      {editable && (
        <TabGroup tabs={[PREDICTED_PARAMS_IKTOS_GENERAL, PREDICTED_PARAMS_IKTOS_INTERMEDIATES]}>
          <TabPanel panelId="0">
            <PredictedParametersIktosGeneral
              params={projectParams}
              tooltipFor={tooltipFor}
              onParamsChanged={(changedParams) => {
                onParamsChanged({ ...projectParams, ...changedParams });
              }}
              buildingBlockElements={buildingBlockElements}
              handleValidationError={handleValidationError}
              defaultMaxNoOfSteps={defaultMaxNoOfSteps}
            />
          </TabPanel>
          <TabPanel panelId="1">
            <PredictedParametersIktosIntermediates
              params={projectParams}
              tooltipFor={tooltipFor}
              onParamsChanged={(changedParams) => {
                onParamsChanged({ ...projectParams, ...changedParams });
              }}
            />
          </TabPanel>
        </TabGroup>
      )}
      {!editable && (
        <div className="rs-readonly-section">
          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              {projectParams.firstDisconnect ? (
                <>First disconnect only</>
              ) : (
                <>
                  <b>{projectParams.maxNoOfSteps}</b> steps per route (up to)
                </>
              )}
            </span>
          </div>

          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              {projectParams.poolChiral ? (
                <>
                  Stereochemistry <b>supported</b>
                </>
              ) : (
                <>
                  Stereochemistry <b>ignored</b>
                </>
              )}
            </span>
          </div>

          <div className="rs-readonly-row">
            <span className="rs-readonly-row__label">
              {projectParams.selectivity ? (
                <>
                  Regioselective <b>reactions only</b>
                </>
              ) : (
                <>
                  Regioselectivity <b>ignored</b>
                </>
              )}
            </span>
          </div>
          {projectParams.usedBuildingBlocks && !projectParams.isRCSUnchecked && (
            <div className="rs-readonly-row">
              <span className="rs-readonly-row__label">
                {projectParams.usedBuildingBlocks.indexOf(RCS10D) >= 0 ? (
                  <>
                    RCS: delivery time <b>up to 10 days</b>
                  </>
                ) : (
                  <>
                    RCS: <b>any</b> delivery time
                  </>
                )}
              </span>
            </div>
          )}
          {!projectParams.isRCSUnchecked && (
            <div className="rs-readonly-row">
              <span className="rs-readonly-row__label">
                RCS:{' '}
                <b>
                  {projectParams.maxPricePerGram && `${projectParams.maxPricePerGram}` !== '0'
                    ? `${projectParams.maxPricePerGram} ($/g)`
                    : 'no'}
                </b>{' '}
                price limit
              </span>
            </div>
          )}
          {projectParams.usedBuildingBlocks &&
            projectParams.usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0).length > 0 && (
              <div className="rs-readonly-row usedBuildingBlocks">
                <span className="rs-readonly-row__label">
                  <b>{projectParams.usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0).join(' ')}</b> building blocks
                </span>
              </div>
            )}
          <div className="rs-readonly-row processingTime">
            <span className="rs-readonly-row__label">
              <b>{getPredictedProcessingTimeLabelByValue(projectParams.processingTime, PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS)}</b>{' '}
              processing time
            </span>
          </div>
          {!(projectParams.includeSubstructures && projectParams.includeSubstructures.length > 0) &&
            !(projectParams.excludeSubstructures && projectParams.excludeSubstructures.length > 0) && (
              <div className="rs-readonly-row includeSubstructures">
                <span className="rs-readonly-row__label">
                  <b>No</b> intermediates defined
                </span>
              </div>
            )}
          {projectParams.includeSubstructures && projectParams.includeSubstructures.length > 0 && (
            <div className="rs-readonly-row includeSubstructures">
              <span className="rs-readonly-row__label">
                <b>{projectParams.includeSubstructures.length}</b> intermediates included
              </span>
            </div>
          )}
          {projectParams.excludeSubstructures && projectParams.excludeSubstructures.length > 0 && (
            <div className="rs-readonly-row includeSubstructures">
              <span className="rs-readonly-row__label">
                <b>{projectParams.excludeSubstructures.length}</b> intermediates excluded
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

PredictedParametersIktos.defaultProps = {
  onParamsChanged: () => null,
  tooltipFor: '',
  handleValidationError: () => null,
};

PredictedParametersIktos.propTypes = {
  optionResponse: PropTypes.array,
  onParamsChanged: PropTypes.func,
  projectParams: PropTypes.object,
  editable: PropTypes.bool,
  tooltipFor: PropTypes.string,
  handleValidationError: PropTypes.func,
};

export default PredictedParametersIktos;
